import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ContentLayout } from '../Layouts/ContentLayout/ContentLayout';
import { NoResults } from '@/components/NoResults/NoResults';
import { PageBody } from '@/components/Page/PageBody/PageBody';
import NoTransactionsIcon from '../../images/NoTransactionsIcon.svg?react';
import { Paginator } from '@/components/Paginator/Paginator';
import { PageFooter } from '@/components/Page/PageFooter/PageFooter';
import { ExecutorWiresList } from './ui/ExecutorWiresList';
import { loadExecutorWires } from './api/api';
import { AdBanner } from '@/bundle/shared/components/banner/AdBanner/AdBanner';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { useHasAnyOrganizationAdminRole } from '@/bundle/shared/hooks/useHasAnyOrganizationAdminRole';
import { Box } from '@/components/Box/Box';
import { SearchInput } from '@/bundle/shared/components/SearchInput/SearchInput';
import { NoSearchResults } from '../shared/components/NoSearchResults';
import { useSearch } from '../shared/hooks/useSearch';
import { getSearchListData } from '@/helpers/helpers';

export const ExecutorWiresPage = () => {
  const { search, setSearch, clearSearch, searchParams, submitSearch } = useSearch<{ name: string }>('name');
  const { hasAnyOrganizationAdminRole } = useHasAnyOrganizationAdminRole();

  const { data, isPending } = useQuery({
    queryKey: ['load_executor_wires', searchParams],
    queryFn: () => loadExecutorWires(searchParams),
    placeholderData: keepPreviousData,
  });

  const handleSubmitSearch = () => {
    if (!!search.length && searchParams.name !== search) {
      amplitudeService.logEvent(AMPLITUDE_EVENTS.ExecutorSearchSubmit);
    }

    submitSearch();
  };

  const { list, hasData, totalCount, hasPagination, noResults, noSearchResults } = getSearchListData(
    data?.body,
    searchParams,
    isPending
  );

  return (
    <ContentLayout
      title='Wires'
      breadcrumbs='Wires'
      subtitle='View the wires assigned to you. Click on the wire card to mark the wire as executed.'
      banner={hasAnyOrganizationAdminRole ? null : <AdBanner />}
      offsetSize='small'
    >
      <PageBody>
        <Box display='flex' alignItems='flex-start' columnGap='20px' flexWrap='wrap'>
          <Box width='360px' mobile={{ width: '100%' }}>
            <SearchInput
              placeholder='Search by wire name'
              value={search}
              onChange={(value) => setSearch(value)}
              onClear={clearSearch}
              onSubmit={handleSubmitSearch}
            />
          </Box>
        </Box>
        {hasData && <ExecutorWiresList list={list} />}
        {noResults && <NoResults icon={<NoTransactionsIcon />} message='There is no wire yet.' />}
        {noSearchResults && <NoSearchResults />}
      </PageBody>
      {hasPagination && (
        <PageFooter>
          <Paginator page={searchParams?.page} limit={searchParams?.limit} total={totalCount} />
        </PageFooter>
      )}
    </ContentLayout>
  );
};
